import React, {Component, Fragment} from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import LoadingOverlay from '../../../shared/components/LoadingOverlay';
import Modal from '../../../shared/ui/Modal';
import addressesActions from '../../store/addresses/action-creators';
import phonesActions from '../../store/phones/action-creators';
import usersActions from '../../store/users/action-creators/index';
import usersSelectors from '../../store/users/selectors';
import AddressAdd from '../common/addresses/AddressAdd';
import AddressEdit from '../common/addresses/AddressEdit';
import AddressList from '../common/addresses/AddressList';
import EditActionButton from '../common/EditActionButton';
import FormattedProperty from '../common/FormattedProperty';
import ListActionSubheader from '../common/ListActionSubheader';
import PhoneAdd from '../common/phones/PhoneAdd';
import PhoneEdit from '../common/phones/PhoneEdit';
import PhoneList from '../common/phones/PhoneList';
import UserRoleList from './UserRoleList';
import AddActionButton from "../common/AddActionButton";
import CustomForm from "../../../shared/components/form/CustomForm";
import {certificationFormConfig} from "../../../shared/components/form/CertificationFormConfig";
import {userEditConfig} from "../../../shared/components/form/UserFormConfig";
import "./UserDetail.scss";
import UploadUserPhoto from './UploadUserPhoto';
import moment from 'moment';
import adminFormatRoleName from "../../../admin-app/models/formatRoleName";
import formatRoleName from "../../models/utilities/formatRoleName";

const formatDate = (date) => {
  return moment(date).format('MM-DD-YYYY')
};

const expiringDays = (certification) => {
  let startAt = moment(certification.startAt);
  let expireAt = moment(certification.expireAt);
  let today = moment();

  if (startAt.valueOf() < today.valueOf() && expireAt.valueOf() > today.valueOf()) {
    return `Expires in ${expireAt.diff(today, 'days')} days`
  } else if (today.valueOf() > expireAt.valueOf()) {
    return `Expired`
  }

};

class UserDetail extends Component {
  constructor(props) {
    super(props);
    this.onAddUserRole = this.onAddUserRole.bind(this);
    this.onRemoveUserRole = this.onRemoveUserRole.bind(this);
    this.onSetSelectedUserRole = this.onSetSelectedUserRole.bind(this);

    this.state = {
      showAddCertificationModal: false,
      showEditCertificationModal: false,
      isLoadingAddCertification: false,
      isLoadingEditCertification: false,
      currentCertification: null,
      isUpdatingUser: false
    }
  }

  // ----------------------
  // Lifecycle
  // ----------------------

  componentDidMount() {
    if (this.props.match.params.userGuid !== this.props.selectedGuid) {
      this.props.setSelectedUser(this.props.match.params.userGuid);
    }
  }

  // ----------------------
  // Handlers
  // ----------------------

  onAddUserRole(roleGuid, trainingProgramGuid) {
    this.props.attachUserRole(this.props.selectedUser.guid, roleGuid, trainingProgramGuid)
      .then(() => {
        if(this.props.selectedUser.guid === this.props.userContext.userGuid) {
          window.location.reload()
        }
      });
  }

  onRemoveUserRole(roleGuid) {
    this.props.detachUserRole(this.props.selectedUser.guid, roleGuid)
      .then(() => {
        if(this.props.selectedUser.guid === this.props.userContext.userGuid) {
          window.location.reload()
        }
      });
  }

  onSaveAddAddress(addressFormData) {
    const formData = {
      addresses: this.props.selectedUser.getAddressesWithAdded(addressFormData),
      userGuid: this.props.selectedGuid,
    };
    this.props.updateUserAddresses(formData);
  }

  onSaveEditAddress(addressFormData) {
    const formData = {
      addresses: this.props.selectedUser.getAddressesWithUpdated(
        this.props.isEditingAddress,
        addressFormData,
      ),
      userGuid: this.props.selectedGuid,
    };
    this.props.updateUserAddresses(formData);
  }

  onSaveAddPhone(phoneFormData) {
    const formData = {
      phones: this.props.selectedUser.getPhonesWithAdded(phoneFormData),
      userGuid: this.props.selectedGuid,
    };
    this.props.updateUserPhones(formData);
  }

  onSaveEditPhone(phoneFormData) {
    const formData = {
      phones: this.props.selectedUser.getPhonesWithUpdated(
        this.props.isEditingPhone,
        phoneFormData,
      ),
      userGuid: this.props.selectedGuid,
    };
    this.props.updateUserPhones(formData);
  }

  onSetSelectedUserRole(roleGuid) {
    this.props.updateUserRole(this.props.selectedUser.guid, roleGuid);
  }

  // ----------------------
  // Computed
  // ----------------------

  get addresses() {
    return this.props.selectedUser.addresses;
  }

  get isLoading() {
    return !this.props.selectedUser || this.props.isLoading.indexOf('fetchUser') > -1;
  }

  get isShowingDetail() {
    return !this.props.isAddingUser;
  }

  get phones() {
    return this.props.selectedUser.getFormattedPhones();
  }

  // ----------------------
  // Methods
  // ----------------------

  setDefaultAddress(index) {
    const formData = {
      addresses: this.props.selectedUser.getAddressesWithDefault(index),
      userGuid: this.props.selectedGuid,
    };
    this.props.updateUserAddresses(formData);
  }

  setDefaultPhone(index) {
    const formData = {
      phones: this.props.selectedUser.getPhonesWithDefault(index),
      userGuid: this.props.selectedGuid,
    };
    this.props.updateUserPhones(formData);
  }

  deleteAddress(index) {
    const formData = {
      addresses: this.props.selectedUser.getAddressesWithDeleted(index),
      userGuid: this.props.selectedGuid,
    };
    this.props.updateUserAddresses(formData);
  }

  deletePhone(index) {
    const formData = {
      phones: this.props.selectedUser.getPhonesWithDeleted(index),
      userGuid: this.props.selectedGuid,
    };
    this.props.updateUserPhones(formData);
  }

  openAddCertification() {
    this.setState({
      showAddCertificationModal: true
    })
  }

  openEditCertification(certification) {
    this.setState({
      showEditCertificationModal: true,
      currentCertification: certification
    })
  }

  saveCertification(formData) {
    this.setState({
      isLoadingAddCertification: true
    });

    this.props.addCertification(formData, this.props.selectedGuid)
      .then(() => {
        this.setState({
          showAddCertificationModal: false,
          isLoadingAddCertification: false
        });
      })
      .catch(() => {
        this.setState({
          isLoadingAddCertification: false
        });
      })
  }

  editCertification(formData) {
    this.setState({
      isLoadingEditCertification: true
    });

    this.props.editCertification({...this.state.currentCertification, ...formData}, this.props.selectedGuid)
      .then(() => {
        this.setState({
          showEditCertificationModal: false,
          isLoadingEditCertification: false
        });
      })
      .catch(() => {
        this.setState({
          isLoadingEditCertification: false
        });
      })
  }

  deleteCertification(certification) {

    this.props.deleteCertification(certification, this.props.selectedGuid)
      .then(() => {

      })
      .catch(() => {

      })
  }

  closeEditCertification() {
    this.setState({
      showEditCertificationModal: false
    })
  }

  closeAddCertification() {
    this.setState({
      showAddCertificationModal: false
    })
  }

  onUpdatePhoto(data) {
    const formData = {
      photo: data.userPhotoUrl,
      userGuid: this.props.selectedGuid,
    };
    this.props.updateUserPhoto(formData);
  }

  saveUserDetails(properties) {
    this.setState({
      isUpdatingUser: true
    });

    this.props.updateUser({
        ...properties,
        userGuid: this.props.selectedUser.guid,
      })
      .then(() => {
        this.setState({
          isUpdatingUser: false
        })
      })
      .catch(() => {
        this.setState({
          isUpdatingUser: false
        })
      });;
  }

  formatUserRoleLocal(role) {
    if(this.props.userContext.scope === 'admin') {
      return adminFormatRoleName(role)
    }
    return formatRoleName(role)
  }

  // ----------------------
  // Render
  // ----------------------

  render() {
    return (
      <LoadingOverlay isLoading={this.isLoading}>
        <Fragment>
          {this.isShowingDetail &&
            !this.isLoading && (
              <Fragment>
                <div className="app-content-header">
                  <h4 className="title">
                    {this.props.selectedUser.getFullName()}
                  </h4>
                  <EditActionButton
                    label="Details"
                    onEdit={() => this.props.editUser(this.props.selectedUser.guid)}
                  />
                </div>
                <div className="photo-details">
                  <UploadUserPhoto
                    onUpdatePhoto={(data) => this.onUpdatePhoto(data)}
                    user={this.props.selectedUser}
                  />
                </div>
                <div className="app-content profile user-details">
                  <ListActionSubheader title="Details" />
                  <div className="details-container">
                    <div className="card profile-details">
                      <div className="profile-details-item">
                        <FormattedProperty
                          label="First Name"
                          value={this.props.selectedUser.firstName}
                        />
                      </div>
                      <div className="profile-details-item">
                        <FormattedProperty
                          label="Last Name"
                          value={this.props.selectedUser.lastName}
                        />
                      </div>
                      <div className="profile-details-item">
                        <FormattedProperty
                          label="Email"
                          value={this.props.selectedUser.email}
                        />
                      </div>
                      <div className="profile-details-item">
                        <FormattedProperty
                          label="Employee ID"
                          value={this.props.selectedUser.employeeIdentifier}
                        />
                      </div>
                    </div>
                  </div>
                  <UserRoleList
                    roles={this.props.selectedUser.roles}
                    onAdd={this.onAddUserRole}
                    onRemove={this.onRemoveUserRole}
                    onSetSelected={this.onSetSelectedUserRole}
                    selectedRoleGuid={this.props.selectedUser.roleGuid}
                  />
                  <PhoneList
                    onAdd={() => this.props.addPhone()}
                    onEdit={index => this.props.editPhone(index)}
                    phones={this.phones}
                    onDelete={index => this.deletePhone(index)}
                    onSetDefault={index => this.setDefaultPhone(index)}
                  />
                  <AddressList
                    onAdd={() => this.props.addAddress()}
                    addresses={this.addresses}
                    onEdit={index => this.props.editAddress(index)}
                    onDelete={index => this.deleteAddress(index)}
                    onSetDefault={index => this.setDefaultAddress(index)}
                  />

                  <ListActionSubheader title="Certifications">
                    <AddActionButton label="certification" onAdd={() => this.openAddCertification()}/>
                  </ListActionSubheader>

                  {this.props.selectedUser.certifications.map((certification) => (
                    <div className="card profile-details">
                      <div className="actions">
                        <EditActionButton label="" onEdit={() => this.openEditCertification(certification)}/>
                        <button
                          className="button danger small"
                          onClick={event => this.deleteCertification(certification)}
                        >
                          <i className="fa fa-close"/>
                          <span>Delete</span>
                        </button>
                      </div>
                      <div className="profile-details-item">
                        <FormattedProperty
                          label="Certification name"
                          value={certification.name}
                        />
                        <FormattedProperty
                          label="Starts at"
                          value={formatDate(certification.startAt)}
                        />
                        <FormattedProperty
                          label="Expires at"
                          value={formatDate(certification.expireAt)}
                        />
                        <div className="expriration-label">{expiringDays(certification)}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <Modal
                  isOpen={this.props.isAddingPhone}
                  onClose={() => this.props.closeAddPhone()}
                  title={`Add Phone for ${this.props.selectedUser.getFullName()}`}
                >
                  <PhoneAdd
                    onSave={formData => this.onSaveAddPhone(formData)}
                  />
                </Modal>

                <Modal
                  isOpen={this.props.isEditingPhone !== -1}
                  onClose={() => this.props.closeEditPhone()}
                  title={`Edit Phone for ${this.props.selectedUser.getFullName()}`}
                >
                  <PhoneEdit
                    phone={
                      this.props.selectedUser.phones[this.props.isEditingPhone]
                    }
                    onSave={formData => this.onSaveEditPhone(formData)}
                  />
                </Modal>

                <Modal
                  isOpen={this.props.isAddingAddress}
                  onClose={() => this.props.closeAddAddress()}
                  title={`Add Address for ${this.props.selectedUser.getFullName()}`}
                >
                  <AddressAdd
                    onSave={formData => this.onSaveAddAddress(formData)}
                  />
                </Modal>

                <Modal
                  isOpen={this.props.isEditingAddress !== -1}
                  onClose={() => this.props.closeEditAddress()}
                  title={`Edit Address for ${this.props.selectedUser.getFullName()}`}
                >
                  <AddressEdit
                    address={this.addresses[this.props.isEditingAddress]}
                    onSave={formData => this.onSaveEditAddress(formData)}
                  />
                </Modal>

                <Modal
                  isOpen={this.props.isEditingUser}
                  onClose={() => this.props.closeEditUser()}
                  title={`Edit User for ${this.props.selectedUser.getFullName()}`}
                >

                  <CustomForm
                    config={userEditConfig}
                    isLoading={this.state.isUpdatingUser}
                    initialValues={this.props.selectedUser}
                    onSubmit={form => this.saveUserDetails(form)}
                  />
                </Modal>
                <Modal
                  isOpen={this.state.showAddCertificationModal}
                  onClose={() => this.closeAddCertification()}
                  title={`Add certification`}
                >
                  <CustomForm
                    config={certificationFormConfig}
                    onSubmit={(formData) => this.saveCertification(formData)}
                    isLoading={this.state.isLoadingAddCertification}
                  />
                </Modal>
                <Modal
                  isOpen={this.state.showEditCertificationModal}
                  onClose={() => this.closeEditCertification()}
                  title={`Edit certification`}
                >
                  {this.state.currentCertification && (
                    <CustomForm
                      config={certificationFormConfig}
                      onSubmit={(formData) => this.editCertification(formData)}
                      isLoading={this.state.isLoadingEditCertification}
                      initialValues={this.state.currentCertification}
                    />
                  )}
                </Modal>
              </Fragment>
            )}
        </Fragment>
      </LoadingOverlay>
    );
  }
}

// ----------------------
// Redux Mappings
// ----------------------

const mapStateToProps = (state) => {
  return {
    isAddingAddress: state.addresses.isAddingAddress,
    isAddingPhone: state.phones.isAddingPhone,
    isEditingAddress: state.addresses.isEditingAddress,
    isEditingPhone: state.phones.isEditingPhone,
    isEditingUser: !!state.users.isEditingUser,
    isLoading: state.users.isLoading,
    selectedGuid: state.users.selectedGuid,
    selectedUser: usersSelectors.selectedUser(state),
    userContext: state.common.userContext,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    addAddress: () => {
      return dispatch(addressesActions.addAddress());
    },
    addPhone: () => {
      return dispatch(phonesActions.addPhone());
    },
    attachUserRole: (userGuid, roleGuid, trainingProgramGuid) => {
      return dispatch(usersActions.attachUserRole(userGuid, roleGuid, trainingProgramGuid));
    },
    closeAddAddress: () => {
      return dispatch(addressesActions.closeAddAddress());
    },
    closeAddPhone: () => {
      return dispatch(phonesActions.closeAddPhone());
    },
    closeEditAddress: () => {
      return dispatch(addressesActions.closeEditAddress());
    },
    closeEditPhone: () => {
      return dispatch(phonesActions.closeEditPhone());
    },
    closeEditUser: () => {
      return dispatch(usersActions.closeEditUser());
    },
    detachUserRole: (userGuid, roleGuid) => {
      return dispatch(usersActions.detachUserRole(userGuid, roleGuid));
    },
    editAddress: (index) => {
      return dispatch(addressesActions.editAddress(index));
    },
    editPhone: (index) => {
      return dispatch(phonesActions.editPhone(index));
    },
    editUser: (userGuid) => {
      return dispatch(usersActions.editUser(userGuid));
    },
    setSelectedUser: (userGuid) => {
      return dispatch(usersActions.setSelectedUser(userGuid));
    },
    updateUser: formData => dispatch(usersActions.updateUser(formData)),
    updateUserAddresses: (formData) => {
      return dispatch(usersActions.updateUserAddresses(formData));
    },
    updateUserPhoto: (formData) => {
      return dispatch(usersActions.updateUserPhoto(formData));
    },
    updateUserPhones: (formData) => {
      return dispatch(usersActions.updateUserPhones(formData));
    },
    updateUserRole: (userGuid, roleGuid) => {
      return dispatch(usersActions.updateUserRole(userGuid, roleGuid));
    },
    addCertification(formData, userId) {
      return dispatch(usersActions.addCertification(formData, userId));
    },
    editCertification(formData, userId) {
      return dispatch(usersActions.editCertification(formData, userId));
    },
    deleteCertification(formData, userId) {
      return dispatch(usersActions.deleteCertification(formData, userId));
    }
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(UserDetail));
